import { createFileRoute, Link } from "@tanstack/react-router";
import { Component, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Activity,
  ArrowLeft,
  Eye,
  Goal,
  MessageSquare,
  Newspaper,
  Radio,
  Users,
  Wallet,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useMode } from "@/lib/mode";
import { RevenueTicker } from "@/components/admin/RevenueTicker";
import { GeoHeatmap } from "@/components/admin/GeoHeatmap";
import { SentimentDashboard } from "@/components/admin/SentimentDashboard";
import { FanInsightsPanel } from "@/components/admin/FanInsightsPanel";
import { AffiliateAuditCenter } from "@/components/admin/AffiliateAuditCenter";
import { AiInsightsPanel } from "@/components/admin/AiInsightsPanel";
import { ExportEngine } from "@/components/admin/ExportEngine";
import { ExecutiveNarrative } from "@/components/admin/ExecutiveNarrative";
import { BusinessAlertsPanel } from "@/components/admin/BusinessAlertsPanel";
import { ApiHealthMonitor } from "@/components/admin/ApiHealthMonitor";
import { GlobalMessageCenter } from "@/components/admin/GlobalMessageCenter";
import { MetricsGlossary } from "@/components/admin/MetricsGlossary";
import { MetricTooltip } from "@/components/admin/MetricTooltip";
import { AdManager } from "@/components/admin/AdManager";
import { ExperienceReliability } from "@/components/admin/ExperienceReliability";
import { SmartBonusAdminPanel } from "@/components/admin/SmartBonusAdminPanel";
import { CompetitivePositioningPanel } from "@/components/admin/CompetitivePositioningPanel";
import { AiCostIntelligencePanel } from "@/components/admin/AiCostIntelligencePanel";
import { SportsOracleAnalyticsPanel } from "@/components/admin/SportsOracleAnalyticsPanel";
import { GlobalOracleAdminPanel } from "@/components/admin/GlobalOracleAdminPanel";
import { PlanOverrideManager } from "@/components/admin/PlanOverrideManager";
import { RewardVoucherManager } from "@/components/admin/RewardVoucherManager";
import { RewardEmailStatus } from "@/components/admin/RewardEmailStatus";
import { HalftimeHubSettings } from "@/components/admin/HalftimeHubSettings";
import { HalftimeKpiDashboard } from "@/components/admin/HalftimeKpiDashboard";
import { CommunityChatModeration } from "@/components/admin/CommunityChatModeration";
import { ExecutiveDemoModePanel } from "@/components/admin/ExecutiveDemoModePanel";
import { BrazilQaTogglePanel } from "@/components/admin/BrazilQaTogglePanel";
import { ProductionReadinessCenter } from "@/components/admin/ProductionReadinessCenter";
import { ExecutiveDealRoom } from "@/components/admin/ExecutiveDealRoom";
import { AdvancedStatsAdminPanel } from "@/components/admin/AdvancedStatsAdminPanel";
import { HeadToHeadAdminPanel } from "@/components/admin/HeadToHeadAdminPanel";
import { MatchClockAdminPanel } from "@/components/admin/MatchClockAdminPanel";
import { FinalQaRehearsalCenter } from "@/components/admin/FinalQaRehearsalCenter";
import { MobileParityQaDashboard } from "@/components/admin/MobileParityQaDashboard";
import { I18nCoveragePanel } from "@/components/admin/I18nCoveragePanel";
import { LanguageDropdown } from "@/components/i18n/LanguageDropdown";
import { SecurityThreatCenter } from "@/components/admin/SecurityThreatCenter";
import { SecurityHardeningPanel } from "@/components/admin/SecurityHardeningPanel";
import { AdvancedSearchPanel } from "@/components/admin/AdvancedSearchPanel";
import { ExecutiveIntelligencePanel } from "@/components/admin/ExecutiveIntelligencePanel";
import { CategoryActivationGovernancePanel } from "@/components/admin/CategoryActivationGovernancePanel";
import { PollsConfigPanel } from "@/components/admin/PollsConfigPanel";
import { BatteryPerformancePanel } from "@/components/admin/BatteryPerformancePanel";
import { MobileLayoutControlPanel } from "@/components/admin/MobileLayoutControlPanel";
import { Pr17MobileLayoutQaPanel } from "@/components/admin/Pr17MobileLayoutQaPanel";
import { SponsorCrmPipeline } from "@/components/admin/SponsorCrmPipeline";
import { MultiTenantCommandCenter } from "@/components/admin/MultiTenantCommandCenter";
import { TenantLicenseProposalCenter } from "@/components/admin/TenantLicenseProposalCenter";
import { TenantExperienceControl } from "@/components/admin/TenantExperienceControl";
import { WorldCupWarRoom } from "@/components/admin/WorldCupWarRoom";
import { WarRoomHealthAlerts } from "@/components/admin/WarRoomHealthAlerts";
import { WarRoomPushAnalytics } from "@/components/admin/WarRoomPushAnalytics";
import { WorldCupOperationsCenter } from "@/components/admin/WorldCupOperationsCenter";
import { WarRoomStabilityPanel } from "@/components/admin/WarRoomStabilityPanel";
import { RegistrationIntelligenceDashboard } from "@/components/admin/RegistrationIntelligenceDashboard";
import { OnboardingAdminPanel } from "@/components/admin/OnboardingAdminPanel";
import { RealSportsDataAdminPanel } from "@/components/admin/RealSportsDataAdminPanel";
import { BettingRevenueDashboard } from "@/components/admin/BettingRevenueDashboard";
import { ContractAutomationCenter } from "@/components/admin/ContractAutomationCenter";
import { PushPipelineAdminPanel } from "@/components/admin/PushPipelineAdminPanel";
import { RetentionOrchestrationCenter } from "@/components/admin/RetentionOrchestrationCenter";
import { PollCommandCenter } from "@/components/admin/PollCommandCenter";
import { MobileUxQaPanel } from "@/components/admin/MobileUxQaPanel";
import { I18nAuditPanel } from "@/components/admin/I18nAuditPanel";
import { CommercialDemoPresets } from "@/components/admin/CommercialDemoPresets";
import { ExecutivePresentationMode } from "@/components/admin/ExecutivePresentationMode";
import { InvestorModePanel } from "@/components/admin/InvestorModePanel";
import { PrivacyComplianceCenter } from "@/components/admin/PrivacyComplianceCenter";
import { DeploymentCenter } from "@/components/admin/DeploymentCenter";
import { PerformanceBenchmarkPanel } from "@/components/admin/PerformanceBenchmarkPanel";
import { MasterReadinessPanel } from "@/components/admin/MasterReadinessPanel";
import { useLanguage } from "@/hooks/useLanguage";
import { trackEvent } from "@/lib/audit";
import { DrilldownDialog } from "@/components/admin/DrilldownDialog";
import { exportCsv, exportJson, exportPdf } from "@/lib/executive/exporters";
import { toast } from "sonner";
import { LegalFooter } from "@/components/brand/LegalFooter";
import { Pr6CompliancePanel } from "@/components/admin/Pr6CompliancePanel";
import { RewardOperationsPanel } from "@/components/admin/RewardOperationsPanel";
import { Pr7QaChecklistPanel } from "@/components/admin/Pr7QaChecklistPanel";
import { Pr8QaChecklistPanel } from "@/components/admin/Pr8QaChecklistPanel";
import { Pr9QaChecklistPanel } from "@/components/admin/Pr9QaChecklistPanel";
import { SuperAdminPinGate } from "@/components/admin/SuperAdminPinGate";
import { requireSuperAdminGuard } from "@/lib/security/tenantIsolation";
import { MatchPollEnginePanel } from "@/components/admin/MatchPollEnginePanel";
import { SponsoredSurveyPanel } from "@/components/admin/SponsoredSurveyPanel";
import { PartnerPortalPermissionsPanel } from "@/components/admin/PartnerPortalPermissionsPanel";
import { ExecutiveAiFormattingPanel } from "@/components/admin/ExecutiveAiFormattingPanel";
import { DailyFeedActionsPanel } from "@/components/admin/DailyFeedActionsPanel";
import { Pr18QaChecklistPanel } from "@/components/admin/Pr18QaChecklistPanel";
import { FanAiGovernancePanel } from "@/components/admin/FanAiGovernancePanel";
import { SuperAdminAiSettingsPanel } from "@/components/admin/SuperAdminAiSettingsPanel";
import { TokenBudgetControlPanel } from "@/components/admin/TokenBudgetControlPanel";
import { PromptTemplateManager } from "@/components/admin/PromptTemplateManager";
import { StrategicAiQaPanel } from "@/components/admin/StrategicAiQaPanel";
import { Pr19QaChecklistPanel } from "@/components/admin/Pr19QaChecklistPanel";
import { SurveyIntelligenceCenter } from "@/components/admin/SurveyIntelligenceCenter";
import { Pr20QaChecklistPanel } from "@/components/admin/Pr20QaChecklistPanel";
import { RegistrationAnalyticsPanel } from "@/components/admin/RegistrationAnalyticsPanel";
import { UserProfileIntelligencePanel } from "@/components/admin/UserProfileIntelligencePanel";
import { AvatarModerationPanel } from "@/components/admin/AvatarModerationPanel";
import { ThemePaletteManager } from "@/components/admin/ThemePaletteManager";
import { LiveEventOverlayPanel } from "@/components/admin/LiveEventOverlayPanel";
import { FloatingSpacingPanel } from "@/components/admin/FloatingSpacingPanel";
import { Pr21QaChecklistPanel } from "@/components/admin/Pr21QaChecklistPanel";
import { ZeroObstructionPanel } from "@/components/admin/ZeroObstructionPanel";
import { ProfileActionsPanel } from "@/components/admin/ProfileActionsPanel";
import { NavigationDeduplicationPanel } from "@/components/admin/NavigationDeduplicationPanel";
import { Pr22QaChecklistPanel } from "@/components/admin/Pr22QaChecklistPanel";
import { PartnerApprovalCenter } from "@/components/admin/PartnerApprovalCenter";
import { Pr23QaChecklistPanel } from "@/components/admin/Pr23QaChecklistPanel";
import { CommandCenterAccordionManager } from "@/components/admin/CommandCenterAccordionManager";
import { CommandCenterAccordion } from "@/components/admin/CommandCenterAccordion";
import { PopularityEnginePanel } from "@/components/admin/PopularityEnginePanel";
import { LeaderboardsPanel } from "@/components/admin/LeaderboardsPanel";
import { BadgesManager } from "@/components/admin/BadgesManager";
import { ReferralEnginePanel } from "@/components/admin/ReferralEnginePanel";
import { ViralGrowthPanel } from "@/components/admin/ViralGrowthPanel";
import { CommunityHeatMapPanel } from "@/components/admin/CommunityHeatMapPanel";
import { Pr24QaChecklistPanel } from "@/components/admin/Pr24QaChecklistPanel";
import { ChatMemoryLifecyclePanel } from "@/components/admin/ChatMemoryLifecyclePanel";
import { SponsorCouponManager } from "@/components/admin/SponsorCouponManager";
import { OffersWalletAnalyticsPanel } from "@/components/admin/OffersWalletAnalyticsPanel";
import { PostMatchRewardsPanel } from "@/components/admin/PostMatchRewardsPanel";
import { LifetimeRewardsLedgerPanel } from "@/components/admin/LifetimeRewardsLedgerPanel";
import { Pr25QaChecklistPanel } from "@/components/admin/Pr25QaChecklistPanel";
import { AthleteTelemetryPanel } from "@/components/admin/AthleteTelemetryPanel";
import { PredictiveAlertsPanel } from "@/components/admin/PredictiveAlertsPanel";
import { WorldCupNewsPublisher } from "@/components/admin/WorldCupNewsPublisher";
import { NotificationCenterAdmin } from "@/components/admin/NotificationCenterAdmin";
import { Mobile3DPerformancePanel } from "@/components/admin/Mobile3DPerformancePanel";
import { HapticsAndSoundPanel } from "@/components/admin/HapticsAndSoundPanel";
import { Pr26QaChecklistPanel } from "@/components/admin/Pr26QaChecklistPanel";
import { TacticalXrayLayoutPanel } from "@/components/admin/TacticalXrayLayoutPanel";
import { TimelineInteractionPanel } from "@/components/admin/TimelineInteractionPanel";
import { EventDeduplicationPanel } from "@/components/admin/EventDeduplicationPanel";
import { FloatingButtonsPanel } from "@/components/admin/FloatingButtonsPanel";
import { Pr27QaChecklistPanel } from "@/components/admin/Pr27QaChecklistPanel";
import { FocusModeControlPanel } from "@/components/admin/FocusModeControlPanel";
import { HalftimePerformancePanel } from "@/components/admin/HalftimePerformancePanel";
import { TranslationAuditPanel } from "@/components/admin/TranslationAuditPanel";
import { MobileBottomLayoutPanel } from "@/components/admin/MobileBottomLayoutPanel";
import { SmartPriorityEnginePanel } from "@/components/admin/SmartPriorityEnginePanel";
import { Pr28QaChecklistPanel } from "@/components/admin/Pr28QaChecklistPanel";
import { TemporaryDismissPanel } from "@/components/admin/TemporaryDismissPanel";
import { CleanScreenModePanel } from "@/components/admin/CleanScreenModePanel";
import { Pr28aQaChecklistPanel } from "@/components/admin/Pr28aQaChecklistPanel";
import { PredictionDisplayPanel } from "@/components/admin/PredictionDisplayPanel";
import { PollCompactPanel } from "@/components/admin/PollCompactPanel";
import { PositiveFeedbackPanel } from "@/components/admin/PositiveFeedbackPanel";
import { CommunityInputSafePanel } from "@/components/admin/CommunityInputSafePanel";
import { FloatingCollisionPanel } from "@/components/admin/FloatingCollisionPanel";
import { DragAndDropPanel } from "@/components/admin/DragAndDropPanel";
import { Pr29QaChecklistPanel } from "@/components/admin/Pr29QaChecklistPanel";
import { Pr29aQaChecklistPanel } from "@/components/admin/Pr29aQaChecklistPanel";
import { ProfileQuickActionsLayoutPanel } from "@/components/admin/ProfileQuickActionsLayoutPanel";
import { ZeroOverlapPolicyPanel } from "@/components/admin/ZeroOverlapPolicyPanel";
import { Timeline2Panel } from "@/components/admin/Timeline2Panel";
import { TimelineContentPanel } from "@/components/admin/TimelineContentPanel";
import { AttentionEnginePanel } from "@/components/admin/AttentionEnginePanel";
import { Pr30QaChecklistPanel } from "@/components/admin/Pr30QaChecklistPanel";
import { MonetizationProtectionPanel } from "@/components/admin/MonetizationProtectionPanel";
import { CleanScreenPolicyPanel } from "@/components/admin/CleanScreenPolicyPanel";
import { MobileStackPanel } from "@/components/admin/MobileStackPanel";
import { PollMiniCardPanel } from "@/components/admin/PollMiniCardPanel";
import { TimelineNarrativePanel } from "@/components/admin/TimelineNarrativePanel";
import { FabDragPanel } from "@/components/admin/FabDragPanel";
import { TranslationAuditPanelPr31 } from "@/components/admin/TranslationAuditPanelPr31";
import { Pr31QaChecklistPanel } from "@/components/admin/Pr31QaChecklistPanel";
import { QuickActionsDockPanel } from "@/components/admin/QuickActionsDockPanel";
import { OverlayPositioningPanel } from "@/components/admin/OverlayPositioningPanel";
import { Timeline4Panel } from "@/components/admin/Timeline4Panel";
import { PollCompactCardPanel } from "@/components/admin/PollCompactCardPanel";
import { FloatingButtonsOrchestratorPanel } from "@/components/admin/FloatingButtonsOrchestratorPanel";
import { CelebrationEnginePanel } from "@/components/admin/CelebrationEnginePanel";
import { SponsorOffersPanel } from "@/components/admin/SponsorOffersPanel";
import { ChatSafeAreaPanel } from "@/components/admin/ChatSafeAreaPanel";
import { HalftimeUltraFastPanel } from "@/components/admin/HalftimeUltraFastPanel";
import { I18nLockPanel } from "@/components/admin/I18nLockPanel";
import { CleanScreenSimplificationPanel } from "@/components/admin/CleanScreenSimplificationPanel";
import { Pr32QaChecklistPanel } from "@/components/admin/Pr32QaChecklistPanel";
import { SportsProviderPanel } from "@/components/admin/SportsProviderPanel";
import { ProductionEnvPanel } from "@/components/admin/ProductionEnvPanel";
import { SupabaseProductionAuditPanel } from "@/components/admin/SupabaseProductionAuditPanel";
import { SentryPanel } from "@/components/admin/SentryPanel";
import { QueuePanel } from "@/components/admin/QueuePanel";
import { DisasterRecoveryPanel } from "@/components/admin/DisasterRecoveryPanel";
import { SecurityHardeningPr33Panel } from "@/components/admin/SecurityHardeningPr33Panel";
import { MultiRegionReadinessPanel } from "@/components/admin/MultiRegionReadinessPanel";
import { InfrastructureHealthDashboard } from "@/components/admin/InfrastructureHealthDashboard";
import { MundialResiliencePanel } from "@/components/admin/MundialResiliencePanel";
import { Pr33QaChecklistPanel } from "@/components/admin/Pr33QaChecklistPanel";
import { RealTimeEventOrchestratorPanel } from "@/components/admin/RealTimeEventOrchestratorPanel";
import { Pr34AQaChecklistPanel } from "@/components/admin/Pr34AQaChecklistPanel";
import { StrategicAutopilotPanel } from "@/components/admin/StrategicAutopilotPanel";
import { BrandPerformancePanel } from "@/components/admin/BrandPerformancePanel";
import { StrategicAlertsPanel } from "@/components/admin/StrategicAlertsPanel";
import { CampaignRecommendationPanel } from "@/components/admin/CampaignRecommendationPanel";
import { Pr34BQaChecklistPanel } from "@/components/admin/Pr34BQaChecklistPanel";
import { ConsumerIntelligencePanel } from "@/components/admin/ConsumerIntelligencePanel";
import { CommunityKnowledgePanel } from "@/components/admin/CommunityKnowledgePanel";
import { Pr34CQaChecklistPanel } from "@/components/admin/Pr34CQaChecklistPanel";
import { PredictiveSportsPanel } from "@/components/admin/PredictiveSportsPanel";
import { Pr34DQaChecklistPanel } from "@/components/admin/Pr34DQaChecklistPanel";
import { ExecutiveReportsPanel } from "@/components/admin/ExecutiveReportsPanel";
import { PartnerIntelligencePanel } from "@/components/admin/PartnerIntelligencePanel";
import { Pr34EQaChecklistPanel } from "@/components/admin/Pr34EQaChecklistPanel";
import { BackgroundJobsPanel } from "@/components/admin/BackgroundJobsPanel";
import { BusinessWarRoomPanel } from "@/components/admin/BusinessWarRoomPanel";
import { Pr34FQaChecklistPanel } from "@/components/admin/Pr34FQaChecklistPanel";
import { BrandingSettingsPanel } from "@/components/admin/BrandingSettingsPanel";
import { Pr35QaChecklistPanel } from "@/components/admin/Pr35QaChecklistPanel";
import { PollLifecycleControlPanel } from "@/components/admin/PollLifecycleControlPanel";
import { PredictionCelebrationPanel } from "@/components/admin/PredictionCelebrationPanel";
import { PollDeduplicationPanel } from "@/components/admin/PollDeduplicationPanel";
import { Pr35AQaChecklistPanel } from "@/components/admin/Pr35AQaChecklistPanel";
import { Pr35BIntegrationPanel } from "@/components/admin/Pr35BIntegrationPanel";
import { Pr35BQaChecklistPanel } from "@/components/admin/Pr35BQaChecklistPanel";
import { MobileSafeLayoutHardLockPanel } from "@/components/admin/MobileSafeLayoutHardLockPanel";
import { MonetizationControlLockPanel } from "@/components/admin/MonetizationControlLockPanel";
import { MobileTabsParityPanel } from "@/components/admin/MobileTabsParityPanel";
import { Pr35CQaChecklistPanel } from "@/components/admin/Pr35CQaChecklistPanel";
import { UserExperienceZonePanel } from "@/components/admin/UserExperienceZonePanel";
import { ThemePersonalizationPanel } from "@/components/admin/ThemePersonalizationPanel";
import { LiveThemeApplicationPanel } from "@/components/admin/LiveThemeApplicationPanel";
import { CinematicColorRulesPanel } from "@/components/admin/CinematicColorRulesPanel";
import { ThemePreviewPanel } from "@/components/admin/ThemePreviewPanel";
import { Pr35D1QaChecklistPanel } from "@/components/admin/Pr35D1QaChecklistPanel";
import { DataCollectionAuditPanel } from "@/components/admin/DataCollectionAuditPanel";
import { Pr35DQaChecklistPanel } from "@/components/admin/Pr35DQaChecklistPanel";
import { MobileClickabilityPanel } from "@/components/admin/MobileClickabilityPanel";
import { ZIndexAuditPanel } from "@/components/admin/ZIndexAuditPanel";
import { FinalOverlapResolutionPanel } from "@/components/admin/FinalOverlapResolutionPanel";
import { Pr35EQaChecklistPanel } from "@/components/admin/Pr35EQaChecklistPanel";
import { EmotionalMatchEnginePanel } from "@/components/admin/EmotionalMatchEnginePanel";
import { LiveNarrativePanel } from "@/components/admin/LiveNarrativePanel";
import { CommunitySentimentPanel } from "@/components/admin/CommunitySentimentPanel";
import { Pr36AQaChecklistPanel } from "@/components/admin/Pr36AQaChecklistPanel";
import { ExperienceModePanel } from "@/components/admin/ExperienceModePanel";
import { ProAnalyticsPresentationPanel } from "@/components/admin/ProAnalyticsPresentationPanel";
import { PredictiveCredibilityPanel } from "@/components/admin/PredictiveCredibilityPanel";
import { Pr36EQaChecklistPanel } from "@/components/admin/Pr36EQaChecklistPanel";
import { UserIdentityPanel } from "@/components/admin/UserIdentityPanel";
import { AdaptiveExperiencePanel } from "@/components/admin/AdaptiveExperiencePanel";
import { PersonalityScoringPanel } from "@/components/admin/PersonalityScoringPanel";
import { Pr36FQaChecklistPanel } from "@/components/admin/Pr36FQaChecklistPanel";
import { SocialGravityEnginePanel } from "@/components/admin/SocialGravityEnginePanel";
import { MassEmotionWavePanel } from "@/components/admin/MassEmotionWavePanel";
import { SocialRetentionPanel } from "@/components/admin/SocialRetentionPanel";
import { CountryBattlePanel } from "@/components/admin/CountryBattlePanel";
import { Pr36GQaChecklistPanel } from "@/components/admin/Pr36GQaChecklistPanel";
import { AiMatchDirectorPanel } from "@/components/admin/AiMatchDirectorPanel";
import { ExperiencePriorityPanel } from "@/components/admin/ExperiencePriorityPanel";
import { VisualSilencePanel } from "@/components/admin/VisualSilencePanel";
import { UserFatiguePanel } from "@/components/admin/UserFatiguePanel";
import { CinematicModePanel } from "@/components/admin/CinematicModePanel";
import { Pr36HQaChecklistPanel } from "@/components/admin/Pr36HQaChecklistPanel";
import { FieldMotionAuditPanel } from "@/components/admin/FieldMotionAuditPanel";
import { CinematicStabilityPanel } from "@/components/admin/CinematicStabilityPanel";
import { SmartCameraMovementPanel } from "@/components/admin/SmartCameraMovementPanel";
import { BallTrackingLimiterPanel } from "@/components/admin/BallTrackingLimiterPanel";
import { MotionSettingsPanel } from "@/components/admin/MotionSettingsPanel";
import { Pr36H1QaChecklistPanel } from "@/components/admin/Pr36H1QaChecklistPanel";
import { EntertainmentHubModePanel } from "@/components/admin/EntertainmentHubModePanel";
import { EntertainmentHubAutoOpenLockPanel } from "@/components/admin/EntertainmentHubAutoOpenLockPanel";
import { EntertainmentHubBackgroundLimiterPanel } from "@/components/admin/EntertainmentHubBackgroundLimiterPanel";
import { EntertainmentHubMobileCompactPanel } from "@/components/admin/EntertainmentHubMobileCompactPanel";
import { Pr36H2BQaChecklistPanel } from "@/components/admin/Pr36H2BQaChecklistPanel";
import { MicroSponsorPreviewPanel } from "@/components/admin/MicroSponsorPreviewPanel";
import { MicroSponsorCriticalLockPanel } from "@/components/admin/MicroSponsorCriticalLockPanel";
import { MicroSponsorTimingPanel } from "@/components/admin/MicroSponsorTimingPanel";
import { Pr36H4BQaChecklistPanel } from "@/components/admin/Pr36H4BQaChecklistPanel";
import { AuthFirstHotfixQaPanel } from "@/components/admin/AuthFirstHotfixQaPanel";
import { SecurityRbacHotfixQaPanel } from "@/components/admin/SecurityRbacHotfixQaPanel";
import { RlsAuditPanel } from "@/components/admin/RlsAuditPanel";
import { TenantIsolationPanel } from "@/components/admin/TenantIsolationPanel";
import { RoleSecurityPanel } from "@/components/admin/RoleSecurityPanel";
import { StorageSecurityPanel } from "@/components/admin/StorageSecurityPanel";
import { SecurityQaChecklistPanel } from "@/components/admin/SecurityQaChecklistPanel";

export const Route = createFileRoute("/admin")({
  beforeLoad: ({ location }) => {
    requireSuperAdminGuard(location.pathname);
  },
  head: () => ({
    meta: [
      { title: "Executive Command Center · VerseSport™" },
      {
        name: "description",
        content: "Executive War Room — KPIs, sentiment, heatmap, revenue y auditoría en tiempo real.",
      },
    ],
  }),
  component: AdminPage,
});

type AuditRow = {
  id: string;
  event_type: string;
  event_data: Record<string, unknown> | null;
  tenant_id: string | null;
  created_at: string;
};

function AdminPage() {
  return (
    <SuperAdminPinGate>
      <AdminBodyBoundary>
        <AdminPageBody />
      </AdminBodyBoundary>
    </SuperAdminPinGate>
  );
}

class AdminBodyBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: { componentStack?: string }) {
    console.error("[AdminBodyBoundary]", error, info);
  }
  reset = () => this.setState({ error: null });
  render() {
    if (!this.state.error) return this.props.children;
    const err = this.state.error;
    return (
      <div className="min-h-screen bg-background text-foreground p-6 flex items-center justify-center">
        <div className="max-w-lg w-full rounded-2xl border border-border bg-card p-6 space-y-3">
          <h1 className="text-base font-black">Panel Admin · error de carga</h1>
          <p className="text-xs text-muted-foreground">
            El PIN se aceptó pero un módulo del dashboard falló al renderizar. Detalle técnico:
          </p>
          <pre className="text-[10px] bg-muted/40 rounded p-2 overflow-auto max-h-48 whitespace-pre-wrap break-words">
            {err.message}
            {err.stack ? `\n\n${err.stack.slice(0, 800)}` : ""}
          </pre>
          <div className="flex gap-2">
            <button onClick={this.reset} className="rounded-md bg-primary px-3 py-2 text-xs font-bold text-primary-foreground">Reintentar</button>
            <a href="/" className="rounded-md border border-input bg-background px-3 py-2 text-xs font-bold">Volver al inicio</a>
          </div>
        </div>
      </div>
    );
  }
}

function AdminPageBody() {
  const [mode] = useMode();
  const { t } = useLanguage();
  const [events, setEvents] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [kpi, setKpi] = useState<{ label: string; value: number; eventKey: string } | null>(null);

  useEffect(() => {
    void trackEvent("i18n_admin_phase_completed", { surface: "admin" });
    void trackEvent("i18n_ai_narratives_completed", { engines: ["live", "decisive", "analog", "oracle"] });
    void trackEvent("realism_engine_upgraded", { version: "v2-spring-inertia" });
    let alive = true;
    const load = async () => {
      const since7d = new Date(Date.now() - 7 * 86_400_000).toISOString();
      const { data } = await supabase
        .from("audit_master")
        .select("id,event_type,event_data,tenant_id,created_at")
        .gte("created_at", since7d)
        .order("created_at", { ascending: false })
        .limit(500);
      if (!alive) return;
      setEvents((data as AuditRow[]) ?? []);
      setLoading(false);
    };
    void load();
    const iv = setInterval(load, 8000);
    return () => {
      alive = false;
      clearInterval(iv);
    };
  }, []);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    events.forEach((e) => {
      c[e.event_type] = (c[e.event_type] ?? 0) + 1;
    });
    return c;
  }, [events]);

  const totalSessions = counts.app_open ?? 0;
  const totalVotes = counts.prediction_vote ?? 0;
  const totalChat = (counts.chat_message ?? 0) + (counts.chat_ai_response ?? 0);
  const totalGoals = counts.goal_celebration ?? 0;
  const totalPredictions = counts.prediction_view ?? 0;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-border/60 glass">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 md:px-6">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="flex h-9 w-9 items-center justify-center rounded-lg glass hover:bg-accent"
              aria-label={t("admin_back")}
            >
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <div>
              <div className="text-sm font-black tracking-tight">{t("admin_title")}</div>
              <div className="text-[10px] font-bold text-gradient-red tracking-widest">
                {t("admin_subtitle")}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div
              className={`hidden md:flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-bold ${
                mode === "betting"
                  ? "bg-[oklch(0.82_0.24_145)]/20 text-[oklch(0.82_0.24_145)]"
                  : "glass"
              }`}
            >
              {mode === "betting" ? <Wallet className="h-3.5 w-3.5" /> : <Newspaper className="h-3.5 w-3.5" />}
              {mode === "media" ? t("admin_media_mode") : t("admin_betting_mode")}
            </div>
            <ExportEngine events={events} />
            <LanguageDropdown />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-6 px-4 py-6 md:px-6">
        {/* PR-13: Executive Command Center 2.0 entry */}
        <Link
          to="/admin/executive-command-center"
          className="block rounded-2xl border border-primary/40 bg-gradient-to-r from-primary/15 to-primary/5 p-4 hover:from-primary/25"
        >
          <div className="text-[10px] font-black tracking-widest text-gradient-red">NEW · PR-13</div>
          <div className="text-base font-black">Executive Command Center 2.0 →</div>
          <div className="text-[11px] text-muted-foreground">
            Ad-Sync Telemetry · Attention (ARI) · Predictive Demand · Category Activation · Revenue · ROI · Export Center.
          </div>
        </Link>

        {/* Executive AI Command Center — Business OS */}
        <Link
          to="/admin/executive-ai-command-center"
          className="block rounded-2xl border border-emerald-500/40 bg-gradient-to-r from-emerald-500/15 to-emerald-500/5 p-4 hover:from-emerald-500/25"
        >
          <div className="text-[10px] font-black tracking-widest text-emerald-400">EXECUTIVE AI · BUSINESS OS</div>
          <div className="text-base font-black">Executive AI Command Center →</div>
          <div className="text-[11px] text-muted-foreground">
            Business · User · Attention · Growth · Monetization · Operational anomalies · AI cost ROI.
          </div>
        </Link>

        {/* PR-13A: Category Activation Governance (Super Admin only) */}
        <CategoryActivationGovernancePanel />

        {/* OPERACIÓN VISIBILIDAD — Operations hub */}
        <section aria-label="Operaciones" className="space-y-3">
          <h2 className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Operaciones</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Link to="/admin/prediction-engine" className="rounded-2xl border border-border/60 glass p-4 hover:bg-accent transition">
              <div className="text-[10px] font-black tracking-widest text-primary">PREDICTION</div>
              <div className="text-base font-black">Prediction Engine →</div>
              <div className="text-[11px] text-muted-foreground">Ticks, eventos, XP, tick log.</div>
            </Link>
            <Link to="/admin/strategic-ai" className="rounded-2xl border border-border/60 glass p-4 hover:bg-accent transition">
              <div className="text-[10px] font-black tracking-widest text-emerald-400">STRATEGIC</div>
              <div className="text-base font-black">Strategic AI →</div>
              <div className="text-[11px] text-muted-foreground">Cockpit y modelos estratégicos.</div>
            </Link>
            <Link to="/admin/qa-report" className="rounded-2xl border border-border/60 glass p-4 hover:bg-accent transition">
              <div className="text-[10px] font-black tracking-widest text-amber-400">QA</div>
              <div className="text-base font-black">QA Report →</div>
              <div className="text-[11px] text-muted-foreground">Checklist y reportes de calidad.</div>
            </Link>
          </div>
        </section>

        {/* OPERACIÓN VISIBILIDAD — Funciones Ocultas (SuperAdmin) */}
        <section aria-label="Funciones Ocultas" className="space-y-3">
          <h2 className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Funciones Ocultas</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Link to="/showcase" className="rounded-xl border border-dashed border-border/60 p-3 hover:bg-accent">
              <div className="text-sm font-bold">Showcase</div>
              <div className="text-[10px] text-muted-foreground">/showcase</div>
            </Link>
            <Link to="/media-blitz" className="rounded-xl border border-dashed border-border/60 p-3 hover:bg-accent">
              <div className="text-sm font-bold">Media Blitz</div>
              <div className="text-[10px] text-muted-foreground">/media-blitz</div>
            </Link>
            <Link to="/social" className="rounded-xl border border-dashed border-border/60 p-3 hover:bg-accent">
              <div className="text-sm font-bold">Social</div>
              <div className="text-[10px] text-muted-foreground">/social</div>
            </Link>
            <Link to="/creator-studio" className="rounded-xl border border-dashed border-border/60 p-3 hover:bg-accent">
              <div className="text-sm font-bold">Creator Studio</div>
              <div className="text-[10px] text-muted-foreground">/creator-studio</div>
            </Link>
            <Link to="/nation-wars" className="rounded-xl border border-dashed border-border/60 p-3 hover:bg-accent">
              <div className="text-sm font-bold">Nation Wars</div>
              <div className="text-[10px] text-muted-foreground">/nation-wars</div>
            </Link>
            <Link to="/viral-cards" className="rounded-xl border border-dashed border-border/60 p-3 hover:bg-accent">
              <div className="text-sm font-bold">Viral Cards</div>
              <div className="text-[10px] text-muted-foreground">/viral-cards</div>
            </Link>
          </div>
        </section>

        {/* PR-15: Smart Poll Engine config */}
        <PollsConfigPanel />

        {/* PR-16: Battery Performance Dashboard + mode toggle */}
        <BatteryPerformancePanel />

        {/* PR-17: Mobile Floating Control System */}
        <MobileLayoutControlPanel />
        <Pr17MobileLayoutQaPanel />

        {/* AI Executive Intelligence Command Center — top of dashboard */}
        <ExecutiveIntelligencePanel />

        {/* Sponsor CRM & Deal Pipeline */}
        <SponsorCrmPipeline />

        {/* Multi-Tenant White-Label Command Center */}
        <MultiTenantCommandCenter />

        {/* Tenant ROI & License Proposal Center */}
        <TenantLicenseProposalCenter />
        <TenantExperienceControl />

        {/* World Cup War Room — live ops cockpit */}
        <WorldCupWarRoom />
        <WarRoomHealthAlerts />
        <WarRoomPushAnalytics />

        {/* World Cup Operations Center — unified ops module (subordinate to Super Admin) */}
        <WorldCupOperationsCenter />

        {/* Enterprise stability cockpit — feature flags, deploy, startup */}
        <WarRoomStabilityPanel />

        {/* Security Threat Center — top priority */}
        <section id="security-center" className="scroll-mt-24">
          <div className="mb-2 flex items-center gap-2">
            <span className="rounded-full bg-[oklch(0.65_0.25_25)]/15 text-[oklch(0.65_0.25_25)] px-2 py-0.5 text-[10px] font-black tracking-widest">
              SECURITY
            </span>
            <span className="text-[11px] text-muted-foreground">
              Threat Center · Honeytokens · Kill Switch · Hardening · Audit Logs
            </span>
          </div>
          <SecurityThreatCenter />
          <SecurityHardeningPanel />
          <AdvancedSearchPanel />
        </section>

        {/* Executive narrative */}
        <ExecutiveNarrative />

        {/* Production Readiness Command Center — top of the war room */}
        <ProductionReadinessCenter />

        {/* Executive Deal Room — revenue & negotiation cockpit */}
        <ExecutiveDealRoom />

        {/* Experience reliability */}
        <ExperienceReliability />

        {/* Monetization pillars */}
        <div className="rounded-2xl glass p-4 text-[12px] leading-relaxed text-muted-foreground">
          <span className="font-bold text-foreground">{t("admin_monetization_engines")} </span>
          {t("admin_monetization_desc")}
        </div>

        {/* Competitive differentiators */}
        <div className="rounded-2xl glass p-4 text-[12px] leading-relaxed text-muted-foreground">
          <div className="mb-2 font-bold text-foreground">{t("admin_competitive_diff")}</div>
          <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
            <li>• {t("admin_diff_radar")}</li>
            <li>• {t("admin_diff_ai")}</li>
            <li>• {t("admin_diff_gamification")}</li>
            <li>• {t("admin_diff_ads")}</li>
            <li>• {t("admin_diff_data")}</li>
            <li>• {t("admin_diff_audit")}</li>
          </ul>
        </div>

        {/* Business alerts */}
        <BusinessAlertsPanel />

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <KPI icon={Users} label={t("kpi_sessions")} value={totalSessions} onOpen={() => setKpi({ label: t("kpi_sessions"), value: totalSessions, eventKey: "app_open" })} tip={{ what: "Sesiones únicas registradas en audit_master.", why: "Define la audiencia second-screen disponible.", impact: "Base para CPM, ARPU y patrocinio." }} />
          <KPI icon={Eye} label={t("kpi_predictions")} value={totalPredictions} onOpen={() => setKpi({ label: t("kpi_predictions"), value: totalPredictions, eventKey: "prediction_view" })} tip={{ what: "Vistas de tarjetas de predicción/encuesta.", why: "Muestra interés editorial y data first-party.", impact: "Eleva el valor del inventario premium." }} />
          <KPI icon={Activity} label={t("kpi_votes")} value={totalVotes} accent onOpen={() => setKpi({ label: t("kpi_votes"), value: totalVotes, eventKey: "prediction_vote" })} tip={{ what: "Interacciones de voto sobre predicciones.", why: "Indicador directo de engagement activo.", impact: "Cada voto incrementa ARPU y data de marca." }} />
          <KPI icon={MessageSquare} label={t("kpi_chat_ai")} value={totalChat} onOpen={() => setKpi({ label: t("kpi_chat_ai"), value: totalChat, eventKey: "chat_message" })} tip={{ what: "Mensajes hacia/desde Sphere AI.", why: "Mide adopción del feature premium.", impact: "Oportunidad de monetización SaaS." }} />
          <KPI icon={Goal} label={t("kpi_goals")} value={totalGoals} onOpen={() => setKpi({ label: t("kpi_goals"), value: totalGoals, eventKey: "goal_celebration" })} tip={{ what: "Celebraciones de gol disparadas en la app.", why: "Picos de atención y emoción.", impact: "Ventana premium para patrocinio." }} />
        </div>

        {/* Revenue ticker */}
        <RevenueTicker boost={totalVotes} />

        {/* API Health */}
        <section id="api-health" className="scroll-mt-24"><ApiHealthMonitor /></section>

        {/* Row: Sentiment + Heatmap */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <SentimentDashboard seed={events.length} />
          <GeoHeatmap seed={events.length + 1} />
        </div>

        {/* Fan Insights */}
        <FanInsightsPanel />

        {/* Row: Affiliate + AI Insights */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <AffiliateAuditCenter />
          </div>
          <AiInsightsPanel />
        </div>

        {/* Global Message Center */}
        <GlobalMessageCenter />

        {/* Ad Manager */}
        <section id="ad-manager" className="scroll-mt-24"><AdManager /></section>

        <CompetitivePositioningPanel />

        <section id="ai-cost-panel" className="scroll-mt-24"><AiCostIntelligencePanel /></section>

        <SportsOracleAnalyticsPanel />

        <GlobalOracleAdminPanel />

        <PlanOverrideManager />

        <RewardVoucherManager />

        <RewardEmailStatus />

        <HalftimeHubSettings />

        <AdvancedStatsAdminPanel />

        <HeadToHeadAdminPanel />

        <MatchClockAdminPanel />

        <FinalQaRehearsalCenter />

        <section id="mobile-parity" className="scroll-mt-24"><MobileParityQaDashboard /></section>

        <section id="i18n-coverage" className="scroll-mt-24"><I18nCoveragePanel /></section>

        <section id="halftime-kpi" className="scroll-mt-24"><HalftimeKpiDashboard /></section>

        <CommunityChatModeration />

        <RegistrationIntelligenceDashboard />

        <OnboardingAdminPanel />

        <RealSportsDataAdminPanel />

        <BettingRevenueDashboard />

        <ContractAutomationCenter />

        <PushPipelineAdminPanel />

        <RetentionOrchestrationCenter />

        <PollCommandCenter />

        <MobileUxQaPanel />

        <I18nAuditPanel />

        <CommercialDemoPresets />

        <ExecutivePresentationMode />

        <InvestorModePanel />

        <PrivacyComplianceCenter />

        <DeploymentCenter />

        <PerformanceBenchmarkPanel />

        <Pr6CompliancePanel />

        <RewardOperationsPanel />

        <Pr7QaChecklistPanel />

        <Pr8QaChecklistPanel />

        <Pr9QaChecklistPanel />

        {/* PR-18 panels */}
        <MatchPollEnginePanel />
        <SponsoredSurveyPanel />
        <PartnerPortalPermissionsPanel />
        <ExecutiveAiFormattingPanel />
        <DailyFeedActionsPanel />
        <Pr18QaChecklistPanel />

        {/* PR-19 panels */}
        <FanAiGovernancePanel />
        <SuperAdminAiSettingsPanel />
        <TokenBudgetControlPanel />
        <PromptTemplateManager />
        <StrategicAiQaPanel />
        <Pr19QaChecklistPanel />

        {/* PR-20 panels */}
        <SurveyIntelligenceCenter />
        <Pr20QaChecklistPanel />

        {/* PR-21 panels */}
        <FloatingSpacingPanel />
        <RegistrationAnalyticsPanel />
        <UserProfileIntelligencePanel />
        <AvatarModerationPanel />
        <ThemePaletteManager />
        <LiveEventOverlayPanel />
        <Pr21QaChecklistPanel />

        {/* PR-22 panels */}
        <ZeroObstructionPanel />
        <ProfileActionsPanel />
        <NavigationDeduplicationPanel />
        <Pr22QaChecklistPanel />

        {/* PR-23 panels */}
        <PartnerApprovalCenter />
        <Pr23QaChecklistPanel />

        {/* PR-24 — Executive Command Center Accordion + Viral Engagement */}
        <CommandCenterAccordionManager />
        <CommandCenterAccordion
          id="user_growth"
          title="User Growth · Popularity Engine"
          defaultOpen
          status="live"
          metrics={[
            { label: "Active fans", value: 5, tone: "success" },
            { label: "Top score", value: 1320, tone: "default" },
          ]}
        >
          <PopularityEnginePanel />
        </CommandCenterAccordion>
        <CommandCenterAccordion
          id="community_intelligence"
          title="Community Intelligence · Leaderboards"
          status="live"
          metrics={[{ label: "Scopes", value: 7 }, { label: "Categories", value: 7 }]}
        >
          <LeaderboardsPanel />
          <div className="h-3" />
          <CommunityHeatMapPanel />
        </CommandCenterAccordion>
        <CommandCenterAccordion
          id="rewards_economy"
          title="Rewards Economy · Titles & Badges"
          metrics={[{ label: "Badges", value: 5 }, { label: "Titles", value: 7 }]}
        >
          <BadgesManager />
        </CommandCenterAccordion>
        <CommandCenterAccordion
          id="audience_intelligence"
          title="Audience Intelligence · Referrals & Viral"
          status="ok"
          metrics={[{ label: "Channels", value: 4 }, { label: "Trending", value: 6, tone: "warning" }]}
        >
          <ReferralEnginePanel />
          <div className="h-3" />
          <ViralGrowthPanel />
        </CommandCenterAccordion>
        <Pr24QaChecklistPanel />

        <CommandCenterAccordion
          id="pr25_chat_coupons_rewards"
          title="PR-25 · Chat Lifecycle · Cupones · Post-Match"
          status="live"
          metrics={[{ label: "Engine", value: "PR-25" }]}
        >
          <ChatMemoryLifecyclePanel />
          <div className="h-3" />
          <SponsorCouponManager />
          <div className="h-3" />
          <OffersWalletAnalyticsPanel />
          <div className="h-3" />
          <PostMatchRewardsPanel />
          <div className="h-3" />
          <LifetimeRewardsLedgerPanel />
        </CommandCenterAccordion>
        <Pr25QaChecklistPanel />

        <CommandCenterAccordion
          id="pr26_athletes_news_alerts"
          title="PR-26 · Telemetría · Alertas · Noticias · Mobile 3D"
          status="live"
          metrics={[{ label: "Engine", value: "PR-26" }]}
        >
          <AthleteTelemetryPanel />
          <div className="h-3" />
          <PredictiveAlertsPanel />
          <div className="h-3" />
          <WorldCupNewsPublisher />
          <div className="h-3" />
          <NotificationCenterAdmin />
          <div className="h-3" />
          <Mobile3DPerformancePanel />
          <div className="h-3" />
          <HapticsAndSoundPanel />
        </CommandCenterAccordion>
        <Pr26QaChecklistPanel />

        <CommandCenterAccordion
          id="pr27_xray_timeline_fabs"
          title="PR-27 · Rayos X · Línea de Tiempo · FABs arrastrables"
          status="live"
          metrics={[{ label: "Engine", value: "PR-27" }]}
        >
          <TacticalXrayLayoutPanel />
          <div className="h-3" />
          <TimelineInteractionPanel />
          <div className="h-3" />
          <EventDeduplicationPanel />
          <div className="h-3" />
          <FloatingButtonsPanel />
        </CommandCenterAccordion>
        <Pr27QaChecklistPanel />

        <CommandCenterAccordion
          id="pr28_focus_perf_i18n_layout"
          title="PR-28 · Focus · Halftime perf · i18n · Bottom layout"
          status="live"
          metrics={[{ label: "Engine", value: "PR-28" }]}
        >
          <FocusModeControlPanel />
          <div className="h-3" />
          <HalftimePerformancePanel />
          <div className="h-3" />
          <TranslationAuditPanel />
          <div className="h-3" />
          <MobileBottomLayoutPanel />
          <div className="h-3" />
          <SmartPriorityEnginePanel />
        </CommandCenterAccordion>
        <Pr28QaChecklistPanel />

        <CommandCenterAccordion
          id="pr28a_temporary_dismiss"
          title="PR-28A · Pantalla Limpia Temporal · Dismiss inteligente"
          status="live"
          metrics={[{ label: "Engine", value: "PR-28A" }]}
        >
          <CleanScreenModePanel />
          <div className="h-3" />
          <TemporaryDismissPanel />
        </CommandCenterAccordion>
        <Pr28aQaChecklistPanel />

        <CommandCenterAccordion
          id="pr29_smart_engagement_drag_fix"
          title="PR-29 · Smart engagement · Safe input · Drag fix"
          status="live"
          metrics={[{ label: "Engine", value: "PR-29" }]}
        >
          <PredictionDisplayPanel />
          <div className="h-3" />
          <PollCompactPanel />
          <div className="h-3" />
          <PositiveFeedbackPanel />
          <div className="h-3" />
          <CommunityInputSafePanel />
          <div className="h-3" />
          <FloatingCollisionPanel />
          <div className="h-3" />
          <DragAndDropPanel />
        </CommandCenterAccordion>
        <Pr29QaChecklistPanel />
        <Pr29aQaChecklistPanel />

        <CommandCenterAccordion
          id="pr30_engagement_dominance"
          title="PR-30 · Engagement dominance · Addictive timeline · Zero overlap"
          status="live"
          metrics={[{ label: "Engine", value: "PR-30" }]}
        >
          <ProfileQuickActionsLayoutPanel />
          <div className="h-3" />
          <ZeroOverlapPolicyPanel />
          <div className="h-3" />
          <Timeline2Panel />
          <div className="h-3" />
          <TimelineContentPanel />
          <div className="h-3" />
          <AttentionEnginePanel />
        </CommandCenterAccordion>
        <Pr30QaChecklistPanel />

        <CommandCenterAccordion
          id="pr31_monetization_protection"
          title="PR-31 · Monetization protection · Addictive UX"
          status="live"
          metrics={[{ label: "Engine", value: "PR-31" }]}
        >
          <MonetizationProtectionPanel />
          <div className="h-3" />
          <CleanScreenPolicyPanel />
          <div className="h-3" />
          <MobileStackPanel />
          <div className="h-3" />
          <PollMiniCardPanel />
          <div className="h-3" />
          <TimelineNarrativePanel />
          <div className="h-3" />
          <FabDragPanel />
          <div className="h-3" />
          <TranslationAuditPanelPr31 />
        </CommandCenterAccordion>
        <Pr31QaChecklistPanel />

        <CommandCenterAccordion
          id="pr32_final_polish"
          title="PR-32 · Final UX polish · Second-screen addiction"
          status="live"
          metrics={[{ label: "Engine", value: "PR-32" }]}
        >
          <QuickActionsDockPanel />
          <div className="h-3" />
          <OverlayPositioningPanel />
          <div className="h-3" />
          <Timeline4Panel />
          <div className="h-3" />
          <PollCompactCardPanel />
          <div className="h-3" />
          <FloatingButtonsOrchestratorPanel />
          <div className="h-3" />
          <HalftimeUltraFastPanel />
          <div className="h-3" />
          <I18nLockPanel />
          <div className="h-3" />
          <CleanScreenSimplificationPanel />
          <div className="h-3" />
          <CelebrationEnginePanel />
          <div className="h-3" />
          <SponsorOffersPanel />
          <div className="h-3" />
          <ChatSafeAreaPanel />
        </CommandCenterAccordion>
        <Pr32QaChecklistPanel />

        <CommandCenterAccordion
          id="pr33_production_infra"
          title="PR-33 · Production infrastructure · Data providers · Monitoring"
          status="live"
          metrics={[{ label: "Engine", value: "PR-33" }]}
        >
          <SportsProviderPanel />
          <div className="h-3" />
          <ProductionEnvPanel />
          <div className="h-3" />
          <SupabaseProductionAuditPanel />
          <div className="h-3" />
          <SentryPanel />
          <div className="h-3" />
          <QueuePanel />
          <div className="h-3" />
          <DisasterRecoveryPanel />
          <div className="h-3" />
          <SecurityHardeningPr33Panel />
          <div className="h-3" />
          <MultiRegionReadinessPanel />
          <div className="h-3" />
          <InfrastructureHealthDashboard />
          <div className="h-3" />
          <MundialResiliencePanel />
        </CommandCenterAccordion>
        <Pr33QaChecklistPanel />

        <RealTimeEventOrchestratorPanel />
        <Pr34AQaChecklistPanel />

        <StrategicAutopilotPanel />
        <BrandPerformancePanel />
        <StrategicAlertsPanel />
        <CampaignRecommendationPanel />
        <Pr34BQaChecklistPanel />

        <ConsumerIntelligencePanel />
        <CommunityKnowledgePanel />
        <Pr34CQaChecklistPanel />

        <PredictiveSportsPanel />
        <Pr34DQaChecklistPanel />

        <ExecutiveReportsPanel />
        <PartnerIntelligencePanel />
        <Pr34EQaChecklistPanel />

        <BusinessWarRoomPanel />
        <BackgroundJobsPanel />
        <Pr34FQaChecklistPanel />

        <BrandingSettingsPanel />
        <Pr35QaChecklistPanel />

        <PollLifecycleControlPanel />
        <PredictionCelebrationPanel />
        <PollDeduplicationPanel />
        <Pr35AQaChecklistPanel />

        <Pr35BIntegrationPanel />
        <Pr35BQaChecklistPanel />

        <MobileSafeLayoutHardLockPanel />
        <MonetizationControlLockPanel />
        <MobileTabsParityPanel />
        <Pr35CQaChecklistPanel />

        <UserExperienceZonePanel />
        <ThemePersonalizationPanel />
        <DataCollectionAuditPanel />
        <Pr35DQaChecklistPanel />

        <LiveThemeApplicationPanel />
        <ThemePreviewPanel />
        <CinematicColorRulesPanel />
        <Pr35D1QaChecklistPanel />

        <MobileClickabilityPanel />
        <ZIndexAuditPanel />
        <FinalOverlapResolutionPanel />
        <Pr35EQaChecklistPanel />

        <EmotionalMatchEnginePanel />
        <LiveNarrativePanel />
        <CommunitySentimentPanel />
        <Pr36AQaChecklistPanel />

        <ExperienceModePanel />
        <ProAnalyticsPresentationPanel />
        <PredictiveCredibilityPanel />
        <Pr36EQaChecklistPanel />

        <UserIdentityPanel />
        <AdaptiveExperiencePanel />
        <PersonalityScoringPanel />
        <Pr36FQaChecklistPanel />

        <SocialGravityEnginePanel />
        <MassEmotionWavePanel />
        <SocialRetentionPanel />
        <CountryBattlePanel />
        <Pr36GQaChecklistPanel />

        <AiMatchDirectorPanel />
        <ExperiencePriorityPanel />
        <VisualSilencePanel />
        <UserFatiguePanel />
        <CinematicModePanel />
        <Pr36HQaChecklistPanel />

        <FieldMotionAuditPanel />
        <CinematicStabilityPanel />
        <SmartCameraMovementPanel />
        <BallTrackingLimiterPanel />
        <MotionSettingsPanel />
        <Pr36H1QaChecklistPanel />

        <EntertainmentHubModePanel />
        <EntertainmentHubAutoOpenLockPanel />
        <EntertainmentHubBackgroundLimiterPanel />
        <EntertainmentHubMobileCompactPanel />
        <Pr36H2BQaChecklistPanel />

        <MicroSponsorPreviewPanel />
        <MicroSponsorCriticalLockPanel />
        <MicroSponsorTimingPanel />
        <Pr36H4BQaChecklistPanel />
        <AuthFirstHotfixQaPanel />
        <SecurityRbacHotfixQaPanel />
        <RlsAuditPanel />
        <RoleSecurityPanel />
        <TenantIsolationPanel />
        <StorageSecurityPanel />
        <SecurityQaChecklistPanel />

        <MasterReadinessPanel />

        <section id="executive-demo" className="scroll-mt-24"><ExecutiveDemoModePanel /></section>

        <BrazilQaTogglePanel />

        {mode === "betting" && <SmartBonusAdminPanel />}

        {/* Live event feed */}
        <div id="live-feed" className="scroll-mt-24 rounded-2xl glass p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Radio className="h-4 w-4 text-primary animate-pulse" />
              <div className="text-xs font-bold tracking-wide">{t("admin_live_feed")}</div>
            </div>
            <span className="text-[10px] text-muted-foreground">
              {loading ? t("admin_loading") : t("admin_events_count", { n: events.length })}
            </span>
          </div>
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-left text-[11px]">
              <thead className="sticky top-0 bg-background/80 text-muted-foreground">
                <tr>
                  <th className="px-2 py-1.5 font-semibold">{t("admin_th_time")}</th>
                  <th className="px-2 py-1.5 font-semibold">{t("admin_th_event")}</th>
                  <th className="px-2 py-1.5 font-semibold">{t("admin_th_tenant")}</th>
                  <th className="px-2 py-1.5 font-semibold">{t("admin_th_data")}</th>
                </tr>
              </thead>
              <tbody>
                {events.slice(0, 50).map((e) => (
                  <tr key={e.id} className="border-t border-border/40">
                    <td className="px-2 py-1.5 tabular-nums text-muted-foreground">
                      {new Date(e.created_at).toLocaleTimeString()}
                    </td>
                    <td className="px-2 py-1.5 font-bold">{e.event_type}</td>
                    <td className="px-2 py-1.5 text-muted-foreground">{e.tenant_id ?? "—"}</td>
                    <td className="px-2 py-1.5 truncate max-w-[280px] text-muted-foreground">
                      {JSON.stringify(e.event_data ?? {})}
                    </td>
                  </tr>
                ))}
                {!loading && events.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-2 py-6 text-center text-muted-foreground">
                      {t("admin_no_events")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Metrics Glossary */}
        <MetricsGlossary />

        <footer className="pt-4 pb-8 text-center text-[10px] text-muted-foreground">
          {t("admin_footer")}
        </footer>
        <LegalFooter />
      </main>

      <button
        type="button"
        onClick={() => {
          const el = document.getElementById("security-center");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          void trackEvent("security_center_shortcut_clicked", { source: "admin_floating_button" });
        }}
        className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 rounded-full bg-[oklch(0.65_0.25_25)] px-4 py-2.5 text-xs font-black tracking-wide text-white shadow-xl shadow-[oklch(0.65_0.25_25)]/30 ring-1 ring-white/10 hover:opacity-90"
        aria-label="Security Center"
      >
        🛡 Security Center
      </button>

      {/* KPI drill-down dialog */}
      <DrilldownDialog
        open={!!kpi}
        onOpenChange={(o) => !o && setKpi(null)}
        title={kpi ? `${t("kpi_drill_title")}: ${kpi.label}` : ""}
        actions={
          kpi
            ? [
                {
                  label: t("dd_export_csv"),
                  onClick: () => {
                    const rows = events
                      .filter((e) => e.event_type === kpi.eventKey)
                      .map((e) => ({
                        time: e.created_at,
                        event: e.event_type,
                        tenant: e.tenant_id ?? "",
                        data: JSON.stringify(e.event_data ?? {}),
                      }));
                    exportCsv(`${kpi.label}.csv`, rows);
                    void trackEvent("export_triggered", { format: "csv", kpi: kpi.eventKey });
                    toast.success("CSV");
                  },
                },
                {
                  label: t("dd_export_pdf"),
                  onClick: () => {
                    exportPdf(
                      kpi.label,
                      `<h2>${kpi.label}</h2><p><b>Total:</b> ${kpi.value}</p>`,
                    );
                    void trackEvent("export_triggered", { format: "pdf", kpi: kpi.eventKey });
                  },
                  variant: "secondary",
                },
                {
                  label: "JSON",
                  onClick: () => {
                    exportJson(`${kpi.label}.json`, {
                      label: kpi.label,
                      value: kpi.value,
                      events: events.filter((e) => e.event_type === kpi.eventKey),
                    });
                    void trackEvent("export_triggered", { format: "json", kpi: kpi.eventKey });
                  },
                  variant: "secondary",
                },
                { label: t("dd_close"), onClick: () => setKpi(null), variant: "secondary" },
              ]
            : []
        }
      >
        {kpi && (
          <>
            <div className="rounded-lg bg-secondary/40 p-3">
              <div className="text-3xl font-black tabular-nums">{kpi.value.toLocaleString()}</div>
              <div className="mt-1 text-[10px] tracking-widest text-muted-foreground">
                {kpi.eventKey}
              </div>
            </div>
            <div>
              <div className="mb-1.5 text-[10px] font-bold tracking-widest text-muted-foreground">
                {t("kpi_drill_trend")}
              </div>
              <KpiTrend events={events} eventKey={kpi.eventKey} />
            </div>
            <p className="text-[11px] text-muted-foreground">{t("kpi_drill_filter")}</p>
          </>
        )}
      </DrilldownDialog>
    </div>
  );
}

function KPI({
  icon: Icon,
  label,
  value,
  accent,
  tip,
  onOpen,
}: {
  icon: typeof Users;
  label: string;
  value: number;
  accent?: boolean;
  tip?: { what: string; why?: string; impact?: string };
  onOpen?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={() => {
        if (onOpen) {
          void trackEvent("kpi_drilldown_opened", { label });
          onOpen();
        }
      }}
      className={`rounded-2xl glass p-3 text-left transition hover:ring-2 hover:ring-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/60 ${accent ? "border-[oklch(0.82_0.24_145)]/40" : ""}`}
      aria-label={label}
    >
      <div className="flex items-center justify-between">
        <Icon className={`h-4 w-4 ${accent ? "text-[oklch(0.82_0.24_145)]" : "text-primary"}`} />
        <div className="flex items-center gap-1">
          <span className="text-[9px] font-bold tracking-widest text-muted-foreground">
            {label.toUpperCase()}
          </span>
          {tip && <MetricTooltip label={label} {...tip} />}
        </div>
      </div>
      <div className="mt-2 text-2xl font-black tabular-nums">{value.toLocaleString()}</div>
    </button>
  );
}

function KpiTrend({ events, eventKey }: { events: AuditRow[]; eventKey: string }) {
  // Bucket the last 12 chronological buckets across matching events
  const matching = events
    .filter((e) => e.event_type === eventKey)
    .map((e) => new Date(e.created_at).getTime())
    .sort();
  if (matching.length === 0) {
    return <div className="rounded-lg bg-background/40 p-3 text-[11px] text-muted-foreground">—</div>;
  }
  const buckets = 12;
  const min = matching[0];
  const max = matching[matching.length - 1];
  const span = Math.max(1, max - min);
  const counts = new Array(buckets).fill(0);
  matching.forEach((ts) => {
    const idx = Math.min(buckets - 1, Math.floor(((ts - min) / span) * buckets));
    counts[idx]++;
  });
  const peak = Math.max(...counts, 1);
  return (
    <div className="flex h-16 items-end gap-1 rounded-lg bg-background/40 p-2">
      {counts.map((c, i) => (
        <div
          key={i}
          className="flex-1 rounded-sm bg-primary/60"
          style={{ height: `${(c / peak) * 100}%` }}
          title={`${c}`}
        />
      ))}
    </div>
  );
}
